import { z } from "zod";
import { ExpirationPolicy, MemberType, ProgramStatus } from "../enums";
import {
  currencyCodeSchema,
  dateWindowIssue,
  isDateWindowOrdered,
  optionalDateSchema,
  optionalHttpUrlSchema,
} from "./common.schema";

/**
 * Validation schema for creating / editing a program. Error messages are
 * translation keys, consistent with the repo's zod conventions.
 */
export const rewardProgramSchema = z
  .object({
    name: z.string().trim().min(1, { message: "program_name_required" }).max(120, {
      message: "max_characters_error",
    }),
    code: z
      .string()
      .min(2, { message: "program_code_required" })
      .regex(/^[A-Z0-9_]+$/, { message: "program_code_format" }),
    description: z.string().max(500, { message: "max_characters_error" }).optional(),
    memberType: z.enum(MemberType),
    status: z.enum(ProgramStatus),
    pointsDisplayName: z.string().trim().min(1, { message: "points_name_required" }),
    pointLabelSingular: z.string().trim().min(1, { message: "point_label_required" }),
    pointLabelPlural: z.string().trim().min(1, { message: "point_label_required" }),
    startDate: optionalDateSchema,
    endDate: optionalDateSchema,
    defaultCurrency: currencyCodeSchema,
    expirationPolicy: z.enum(ExpirationPolicy),
    pointsExpirationDays: z
      .number()
      .int()
      .positive({ message: "expiration_days_invalid" })
      .nullable()
      .optional(),
    timeZone: z.string().min(1, { message: "timezone_required" }),
    termsUrl: optionalHttpUrlSchema,
    termsContent: z.string().nullable().optional(),
  })
  .refine(
    (data) =>
      data.expirationPolicy !== ExpirationPolicy.FIXED_DURATION ||
      (data.pointsExpirationDays != null && data.pointsExpirationDays > 0),
    { message: "expiration_days_required", path: ["pointsExpirationDays"] },
  )
  .refine(isDateWindowOrdered, dateWindowIssue());

export type RewardProgramFormValues = z.infer<typeof rewardProgramSchema>;
